import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import './Toast.css';

const ToastContext = createContext(null);

export function useToast() {
    const context = useContext(ToastContext);
    if (!context) throw new Error('useToast must be used within ToastProvider');
    return context;
}

export default function ToastProvider({ children }) {
    const [toasts, setToasts] = useState([]);
    const idRef = useRef(0);
    const timersRef = useRef(new Map());

    // Clear all timers on unmount
    useEffect(() => {
        const timers = timersRef.current;
        return () => {
            timers.forEach(timerId => clearTimeout(timerId));
            timers.clear();
        };
    }, []);

    const showToast = useCallback((message, type = 'success', duration = 4000, action = null) => {
        const id = ++idRef.current;
        setToasts(prev => [...prev, { id, message, type, action }]);
        const timerId = setTimeout(() => {
            setToasts(prev => prev.filter(t => t.id !== id));
            timersRef.current.delete(id);
        }, duration);
        timersRef.current.set(id, timerId);
    }, []);

    const dismissToast = useCallback((id) => {
        setToasts(prev => prev.filter(t => t.id !== id));
        const timerId = timersRef.current.get(id);
        if (timerId) {
            clearTimeout(timerId);
            timersRef.current.delete(id);
        }
    }, []);

    return (
        <ToastContext.Provider value={{ showToast }}>
            {children}
            <div className="toast-container">
                {toasts.map(toast => (
                    <div
                        key={toast.id}
                        className={`toast toast-${toast.type}`}
                        onClick={() => dismissToast(toast.id)}
                    >
                        <span className="toast-icon">
                            {toast.type === 'success' && '\u2713'}
                            {toast.type === 'error' && '\u2717'}
                            {toast.type === 'warning' && '!'}
                            {toast.type === 'info' && '\u2139'}
                        </span>
                        <span className="toast-message">{toast.message}</span>
                        {toast.action && (
                            <button
                                className="toast-action"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    dismissToast(toast.id);
                                    toast.action.onClick();
                                }}
                            >
                                {toast.action.label}
                            </button>
                        )}
                    </div>
                ))}
            </div>
        </ToastContext.Provider>
    );
}
